import { request } from './client';
import type { PagedResponse } from './unitsOfMeasure';

/** Một quốc gia. */
export interface Country {
  readonly id: string;
  readonly code: string;
  readonly codeAlpha3: string | null;
  readonly name: string;
  readonly nameEn: string;
  readonly phoneCode: string | null;
  readonly defaultCurrencyCode: string | null;
  readonly isActive: boolean;
  readonly sortOrder: number;
}

/** Điều kiện lọc danh sách quốc gia. */
export interface CountryFilter {
  readonly searchTerm?: string;
  readonly includeInactive?: boolean;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

/** Nội dung gửi lên khi thêm quốc gia. */
export interface CreateCountryPayload {
  readonly code: string;
  readonly codeAlpha3: string | null;
  readonly name: string;
  readonly nameEn: string;
  readonly phoneCode: string | null;
  readonly defaultCurrencyCode: string | null;
  readonly sortOrder: number;
}

/** Nội dung gửi lên khi sửa quốc gia. Không có mã hai chữ: nó không sửa được. */
export interface UpdateCountryPayload {
  readonly codeAlpha3: string | null;
  readonly name: string;
  readonly nameEn: string;
  readonly phoneCode: string | null;
  readonly defaultCurrencyCode: string | null;
  readonly sortOrder: number;
}

const baseRoute = '/api/v1/platform/countries';

/** Dựng phần truy vấn của địa chỉ, bỏ hẳn tham số rỗng. */
function buildQueryString(filter: CountryFilter): string {
  const parameters = new URLSearchParams();

  if (filter.searchTerm !== undefined && filter.searchTerm.trim() !== '') {
    parameters.set('searchTerm', filter.searchTerm.trim());
  }

  if (filter.includeInactive === true) {
    parameters.set('includeInactive', 'true');
  }

  if (filter.pageNumber !== undefined) {
    parameters.set('pageNumber', String(filter.pageNumber));
  }

  if (filter.pageSize !== undefined) {
    parameters.set('pageSize', String(filter.pageSize));
  }

  const query = parameters.toString();

  return query === '' ? '' : `?${query}`;
}

/** Lấy một trang danh sách quốc gia. */
export async function listCountries(
  filter: CountryFilter,
): Promise<PagedResponse<Country>> {
  return await request<PagedResponse<Country>>(`${baseRoute}${buildQueryString(filter)}`);
}

/** Thêm một quốc gia. */
export async function createCountry(payload: CreateCountryPayload): Promise<Country> {
  return await request<Country>(baseRoute, { method: 'POST', body: payload });
}

/** Sửa một quốc gia. */
export async function updateCountry(
  id: string,
  payload: UpdateCountryPayload,
): Promise<Country> {
  return await request<Country>(`${baseRoute}/${id}`, { method: 'PUT', body: payload });
}

/** Ngừng dùng hoặc cho dùng lại một quốc gia. */
export async function setCountryActive(id: string, isActive: boolean): Promise<Country> {
  return await request<Country>(`${baseRoute}/${id}/active`, {
    method: 'PUT',
    body: { isActive },
  });
}
